import { useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchBroadcasts, sendBroadcast } from '../api';

// Admin-only bulk messaging panel.
//   - Choose a channel (Email or SMS), compose, send to all opted-in members.
//   - Email uses a subject + body; SMS is body-only.
//   - Shows a history of recent broadcasts (recipient/sent/failed counts).
//
// Recipient selection + opt-out compliance are enforced server-side; this UI
// just composes and reports. SMS delivery depends on Twilio A2P 10DLC carrier
// registration; if it's not configured the server returns a clear 503.

// SMS segment accounting: single GSM-7 segment is 160 chars; concatenated
// messages drop to 153/segment. The server also appends "Reply STOP to opt out."
const SMS_STOP_FOOTER_LEN = 24; // "\nReply STOP to opt out."

function fmt(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString();
}

function smsSegments(len) {
  if (len <= 0) return 0;
  if (len <= 160) return 1;
  return Math.ceil(len / 153);
}

export default function AdminBroadcast() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const accountKey = account?.homeAccountId || account?.username || null;

  const [channel, setChannel] = useState('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [banner, setBanner] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { ok, data } = await fetchBroadcasts(instance, account);
      if (ok) setHistory(data?.broadcasts || []);
    } catch { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const isSms = channel === 'sms';
  const bodyLen = body.trim().length;
  const projectedSmsLen = bodyLen + SMS_STOP_FOOTER_LEN;
  const canSend = isSms ? !!body.trim() : (!!subject.trim() && !!body.trim());

  async function handleSend() {
    const s = subject.trim();
    const b = body.trim();
    if (isSms ? !b : (!s || !b)) {
      setBanner({ kind: 'warn', text: isSms ? 'Message is required.' : 'Subject and message are both required.' });
      return;
    }
    const confirmText = isSms
      ? 'Send this text message to all SMS-opted-in members? Standard carrier rates may apply to recipients.'
      : 'Send this email to all opted-in members?';
    if (!window.confirm(confirmText)) return;

    setSending(true);
    setBanner(null);
    try {
      const payload = isSms ? { channel, body: b } : { channel, subject: s, body: b };
      const { ok, data, status } = await sendBroadcast(instance, account, payload);
      if (ok) {
        setBanner({
          kind: 'ok',
          text: `Sent to ${data.sentCount}/${data.recipientCount} members${data.failedCount ? `, ${data.failedCount} failed` : ''}.`,
        });
        setSubject('');
        setBody('');
        loadHistory();
      } else {
        setBanner({ kind: 'warn', text: (data && (data.message || data.error)) || `HTTP ${status}` });
      }
    } catch (e) {
      setBanner({ kind: 'warn', text: e.message });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="card">
      <h2>Bulk Messaging</h2>

      <div className="channel-toggle" role="tablist" aria-label="Broadcast channel">
        <button
          role="tab"
          aria-selected={!isSms}
          className={!isSms ? 'active' : ''}
          onClick={() => setChannel('email')}
        >
          Email
        </button>
        <button
          role="tab"
          aria-selected={isSms}
          className={isSms ? 'active' : ''}
          onClick={() => setChannel('sms')}
        >
          SMS
        </button>
      </div>

      <p className="muted">
        {isSms
          ? 'Sends a text to all active members who opted into SMS and have a phone on file. "Reply STOP to opt out." is appended automatically.'
          : "Sends to all active members who haven't opted out of email. A club footer is added automatically."}
      </p>

      {banner && <div className={`notice ${banner.kind === 'ok' ? '' : 'warn'}`}>{banner.text}</div>}

      <div className="broadcast-form">
        {!isSms && (
          <input
            type="text"
            placeholder="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        )}
        <textarea
          rows={5}
          placeholder={isSms ? 'Write your text message…' : 'Write your message to the members…'}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        {isSms && (
          <p className="muted sms-meter">
            {projectedSmsLen} chars incl. opt-out footer · ~{smsSegments(projectedSmsLen)} segment{smsSegments(projectedSmsLen) === 1 ? '' : 's'} per recipient
          </p>
        )}
        <button disabled={sending || !canSend} onClick={handleSend}>
          {sending ? 'Sending…' : (isSms ? 'Send text to members' : 'Send email to members')}
        </button>
      </div>

      {history.length > 0 && (
        <div className="broadcast-history">
          <h3>Recent sends</h3>
          <ul>
            {history.map((h) => (
              <li key={h.id}>
                <strong>{h.channel === 'sms' ? '📱 SMS' : (h.subject || '(no subject)')}</strong>
                <span className="muted"> · {h.sent_count}/{h.recipient_count} sent{h.failed_count ? `, ${h.failed_count} failed` : ''} · {fmt(h.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
